import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import {
  Layout,
  Space,
  Card,
  Row,
  Col,
  PageHeader,
  Empty,
  Typography,
  Drawer,
  message,
  Button,
  Descriptions,
  Select,
} from "antd";

import { SUBGRAPH_DETAIL_AND_PROJECT_AND_INDEXING_STATUS_QUERY } from "../../helpers/graphql/aggregate";
import { DeleteSubgraph } from "../../components/modal/DeleteSubgraph";
import { SubgraphStatus } from "../../components/subgraph/SubgraphStatus";
import { SubgraphInfo } from "../../components/subgraph/SubgraphInfo";
import { SubgraphDeployment } from "../../components/drawer/SubgraphDeployment";
import { SubgraphLogs } from "../../components/subgraph/SubgraphLogs";
import { SubgraphMetrics } from "../../components/subgraph/SubgraphMetrics";
import { SubgraphOriginMetrics } from "../../components/subgraph/SubgraphOriginMetrics";
import { timeIntervalOption, timeRange } from "../../helpers/metrics.option";

export function SubgraphDetail() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [interval, setInterval] = useState("15m");
  const [showLast, setShowLast] = useState("_1d");

  const location = useLocation();
  const cluster = /subgraphs-v2/.test(location.pathname) ? 'v2' : 'v1';

  const { projectId, subgraphNamespace, subgraphIdentifier } = useParams();
  const subgraphId = `${subgraphNamespace}/${subgraphIdentifier}`;

  const { loading, data, error } = useQuery(SUBGRAPH_DETAIL_AND_PROJECT_AND_INDEXING_STATUS_QUERY, {
    variables: { projectId: projectId, subgraphId: subgraphId, cluster },
    pollInterval: 5000,
    errorPolicy: "all",
  });

  useEffect(() => {
    if (error) {
      message.error(error.message.toString());
    }
  }, [error]);

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
  };

  const subgraph = data?.subgraph;
  const project = data?.project;

  const indexingStatusCurrent = data?.subgraphIndexingStatusForCurrentVersion;
  const indexingStatusPending = data?.subgraphIndexingStatusForPendingVersion;

  const renderTitle = () => {
    return (
      <Row align="middle">
        <Col flex={1}>
          {/* Subgraph name */}
          <Typography.Title>{subgraph.name}</Typography.Title>
        </Col>
        <Col flex={6} style={{ textAlign: "end" }}>
          {indexingStatusCurrent?.queryUrl ? (
            <Button type="primary" onClick={() => window.open(indexingStatusCurrent?.queryUrl)}>
              Query
            </Button>
          ) : null}

          {/* Deployment steps */}
          {indexingStatusCurrent ? (
            <Button type="ghost" onClick={showDrawer} style={{ marginLeft: 10 }}>
              Deploy
            </Button>
          ) : null}
        </Col>
      </Row>
    );
  };

  return (
    <Layout className="content">
      <Layout.Header className="content-header">
        <Row>
          <Col span={12}>
            <PageHeader
              title="Subgraph Detail"
              className="page-title"
              onBack={() => navigate(-1)}
            />
          </Col>
          {/* Delete subgraph */}
          <Col span={12} style={{ textAlign: "end" }}>
            {subgraph ? (
              <DeleteSubgraph subgraphId={subgraph?.id} projectId={subgraph?.projectId} cluster={cluster} />
            ) : null}
          </Col>
        </Row>
      </Layout.Header>
      <Layout.Content className="content-body">
        <Drawer
          title="Deployment steps"
          placement="right"
          onClose={onClose}
          visible={visible}
          size={"large"}
        >
          <SubgraphDeployment project={project} subgraph={subgraph} />
        </Drawer>

        {loading ? (
          <Card loading={true} className="card-loading" />
        ) : subgraph ? (
          <Space
            direction="vertical"
            style={{
              width: "100%",
            }}
          >
            <Row gutter={10}>
              <Col
                xs={24}
                sm={24}
                lg={indexingStatusCurrent ? 24 : 24}
                xl={indexingStatusCurrent ? 11 : 24}
              >
                <Card className="card-body" title={renderTitle()}>
                  <SubgraphInfo
                    project={project}
                    subgraph={subgraph}
                    indexingStatus={indexingStatusCurrent}
                  />
                </Card>
              </Col>
              <Col
                xs={24}
                sm={24}
                lg={indexingStatusCurrent ? 24 : 24}
                xl={indexingStatusCurrent ? 13 : 24}
              >
                {indexingStatusCurrent ? (
                  <Row>
                    <Col md={12} xs={24}>
                      <Descriptions layout="horizontal" column={2}>
                        <Descriptions.Item label="Interval">
                          <Select
                            size="small"
                            defaultValue={interval}
                            onChange={(value) => setInterval(value)}
                            options={timeIntervalOption}
                          />
                        </Descriptions.Item>
                        <Descriptions.Item label="Show last">
                          <Select
                            size="small"
                            defaultValue={showLast}
                            onChange={(value) => setShowLast(value)}
                            options={timeRange}
                          />
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>
                  </Row>
                ) : null}
                <Row gutter={8}>
                  <Col
                    xs={24}
                    sm={24}
                    lg={indexingStatusCurrent ? 12 : 24}
                    xl={indexingStatusCurrent ? 12 : 24}
                  >
                    {indexingStatusCurrent ? (
                      <SubgraphMetrics
                        cluster={cluster}
                        subgraphId={subgraphId}
                        height={190}
                        interval={interval}
                        showLast={showLast}
                      />
                    ) : null}
                    {!indexingStatusCurrent ? (
                      <SubgraphDeployment project={project} subgraph={subgraph} />
                    ) : null}
                  </Col>
                  <Col
                    xs={24}
                    sm={24}
                    lg={indexingStatusCurrent ? 12 : 24}
                    xl={indexingStatusCurrent ? 12 : 24}
                  >
                    {indexingStatusCurrent ? (
                      <SubgraphOriginMetrics
                        cluster={cluster}
                        subgraphId={subgraphId}
                        height={190}
                        interval={interval}
                        showLast={showLast}
                      />
                    ) : null}
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row gutter={10}>
              {indexingStatusCurrent ? (
                <Col md={24} xxl={indexingStatusPending ? 12 : 24}>
                  <SubgraphStatus
                    title="Current version status"
                    themeColor="#fff"
                    textColor="#000"
                    project={project}
                    subgraph={subgraph}
                    indexingStatus={indexingStatusCurrent}
                  />
                  <SubgraphLogs
                    subgraph={subgraph}
                    indexingStatus={indexingStatusCurrent}
                    themeColor="#fff"
                    textColor="#000"
                  />
                </Col>
              ) : null}

              {indexingStatusPending ? (
                <Col md={24} xxl={12}>
                  <SubgraphStatus
                    title="Pending version status"
                    themeColor="#001529"
                    textColor="#fff"
                    project={project}
                    subgraph={subgraph}
                    indexingStatus={indexingStatusPending}
                  />
                  <SubgraphLogs
                    subgraph={subgraph}
                    indexingStatus={indexingStatusPending}
                    themeColor="#001529"
                    textColor="#fff"
                  />
                </Col>
              ) : null}
            </Row>
          </Space>
        ) : (
          <Empty />
        )}
      </Layout.Content>
    </Layout>
  );
}
